import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "411",
  "slug": "what-is-the-prime-meridian",
  "title": "What Is the Prime Meridian? The Invisible Line That Sets the World’s Clocks",
  "excerpt": "There is no natural starting point for measuring east and west — so the world agreed to invent one. Here is the story of the prime meridian and why it runs through a London suburb.",
  "date": "2026-06-11",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1642582466531-92118d16f5df?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxncmVlbndpY2glMjBvYnNlcnZhdG9yeSUyMGxvbmRvbnxlbnwxfDB8fHwxNzgxMTY1NTQ1fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The prime meridian is the line of 0° longitude, running through Greenwich, London. Unlike the equator, it has no natural basis — it was chosen by international agreement at the 1884 International Meridian Conference. It anchors both global longitude and the world’s time zones, since time is measured east and west from Greenwich.",
  "content": [
    {
      "type": "paragraph",
      "text": "The equator picks itself: it is the natural line halfway between the poles, fixed by the way the Earth spins. Longitude has no such gift. There is nothing in nature that says east-west measurement should start in one place rather than another. So the world had to choose a starting line by agreement — and the line it chose, the prime meridian, runs through a hilltop observatory in a London suburb. Here is how an arbitrary decision became one of the most important lines on Earth."
    },
    {
      "type": "heading",
      "text": "What the prime meridian actually is"
    },
    {
      "type": "paragraph",
      "text": "The prime meridian is the line of 0° longitude. It runs from the North Pole to the South Pole, passing through Greenwich in London, and it is the reference from which every other line of longitude is measured east or west, all the way around to 180° on the far side of the planet. Pair a longitude reading with a latitude reading and you can pinpoint any spot on the surface of the Earth — but only because everyone agrees on where zero is."
    },
    {
      "type": "heading",
      "text": "Why Greenwich?"
    },
    {
      "type": "paragraph",
      "text": "For centuries, different countries used their own prime meridians, often running through their own capital cities — Paris, Washington, Rio, and many more. That was a recipe for confusion, especially for ships navigating across oceans using charts drawn to different zero points. As British sea power and the Royal Observatory at Greenwich came to dominate global navigation in the 1800s, more and more charts were already drawn relative to Greenwich. By the time the world sat down to standardise, Greenwich was the practical front-runner."
    },
    {
      "type": "heading",
      "text": "The 1884 conference that settled it"
    },
    {
      "type": "paragraph",
      "text": "The matter was decided at the International Meridian Conference, held in Washington, D.C. in 1884, where delegates from 25 nations voted to adopt the meridian through Greenwich as the world's prime meridian. The vote was not unanimous — France, championing its own Paris meridian, abstained and continued to use it in official contexts for years, not formally adopting Greenwich until 1911. But the decision stuck, and Greenwich has anchored global longitude ever since."
    },
    {
      "type": "heading",
      "text": "The link to time"
    },
    {
      "type": "paragraph",
      "text": "The prime meridian's quiet superpower is that it also anchors the world's clocks. Because the Earth rotates 360° in 24 hours, it turns 15° of longitude every hour — which means longitude and time are really the same measurement in different units. Global time is reckoned east and west from Greenwich, which is why the world's time standard grew out of Greenwich Mean Time and why time zones are, at heart, slices of longitude. The same conference that fixed the prime meridian set the stage for the global system of time zones we still use today."
    },
    {
      "type": "list",
      "items": [
        "Longitude runs 0° to 180° east and 0° to 180° west of the prime meridian.",
        "The 180° line on the opposite side of the Earth roughly follows the International Date Line.",
        "The Earth turns 15° of longitude per hour, the reason each time zone is about 15° wide.",
        "Greenwich Mean Time (GMT) and the modern Coordinated Universal Time (UTC) both take Greenwich as their reference."
      ]
    },
    {
      "type": "heading",
      "text": "What the line actually passes through"
    },
    {
      "type": "paragraph",
      "text": "For a line that defines half the planet's coordinate grid, the prime meridian touches surprisingly little land. Heading south from the North Pole it crosses the United Kingdom, clips eastern France and Spain, then runs down through Algeria and Mali, across Burkina Faso, Togo, and Ghana, before leaving the West African coast and running the long, empty stretch of the Atlantic and on to Antarctica. Standing exactly on 0° longitude is therefore a rare experience — most of the line is over open ocean or ice — which is part of why the marked strip at Greenwich draws such crowds. It is one of the only convenient places on Earth to put one foot in the eastern hemisphere and one in the western."
    },
    {
      "type": "heading",
      "text": "A line that has quietly moved"
    },
    {
      "type": "paragraph",
      "text": "Here is a detail that delights map nerds: the brass line you can stand astride at the Royal Observatory is not exactly where 0° longitude sits today. Modern satellite positioning uses a slightly different reference surface for the Earth, and the result is that the true zero of longitude now lies a little over a hundred metres east of the historic Greenwich line. Visitors line up to photograph themselves on the famous strip, while the GPS in their pocket insists the real prime meridian is just down the path. It is a perfect reminder that the prime meridian was always a human decision, refined as our tools improved."
    }
  ]
};
